import {
  BarChart03,
  Database01,
  FileSearch02,
  Inbox01,
} from '@untitledui/icons';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { cx } from '@/utils/cx';

export interface EmptyStateProps {
  variant?: 'default' | 'noData' | 'noResults' | 'empty';
  title?: string;
  description?: string;
  icon?: ReactNode;
  iconColor?: 'gray' | 'brand' | 'error' | 'warning' | 'success';
  action?: {
    label: string;
    onClick: () => void;
    icon?: React.FC<{ className?: string }>;
    /** Button color. Defaults to 'secondary'. */
    color?: 'primary' | 'secondary';
    /** Disables the action button. */
    isDisabled?: boolean;
    /** Tooltip shown when the action button is disabled. */
    disabledTooltip?: ReactNode;
  };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const VARIANT_DEFAULTS = {
  default: {
    icon: Database01,
    title: 'No data available',
    description: "There's nothing here yet",
  },
  noData: {
    icon: BarChart03,
    title: 'No data available',
    description: 'Get started by adding some data',
  },
  noResults: {
    icon: FileSearch02,
    title: 'No results found',
    description: 'Try adjusting your search or filters',
  },
  empty: {
    icon: Inbox01,
    title: 'Nothing here yet',
    description: 'Items will appear here once added',
  },
};

const SIZE_CONFIG = {
  sm: {
    container: 'py-8',
    title: 'text-sm',
    description: 'text-xs',
    iconSize: 'md' as const,
  },
  md: {
    container: 'py-12',
    title: 'text-base',
    description: 'text-sm',
    iconSize: 'lg' as const,
  },
  lg: {
    container: 'py-16',
    title: 'text-lg',
    description: 'text-base',
    iconSize: 'xl' as const,
  },
};

export function EmptyState({
  variant = 'default',
  title,
  description,
  icon,
  iconColor = 'gray',
  action,
  size = 'md',
  className,
}: EmptyStateProps) {
  const variantConfig = VARIANT_DEFAULTS[variant];
  const sizeConfig = SIZE_CONFIG[size];

  const finalTitle = title || variantConfig.title;
  // `undefined` falls back to the variant default; an explicit empty string
  // (`description=''`) suppresses the description paragraph entirely.
  const finalDescription = description ?? variantConfig.description;

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
      animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cx(
        'flex flex-col items-center justify-center text-center',
        sizeConfig.container,
        className
      )}
    >
      {/* Icon */}
      <div className='mb-4'>
        {icon ? (
          icon
        ) : (
          <FeaturedIcon
            icon={variantConfig.icon}
            size={sizeConfig.iconSize}
            theme='light-outline'
            color={iconColor}
          />
        )}
      </div>

      {/* Title */}
      <h3
        className={cx(
          'font-semibold text-[var(--color-text-secondary)]',
          sizeConfig.title
        )}
      >
        {finalTitle}
      </h3>

      {/* Description */}
      {finalDescription && (
        <p
          className={cx(
            'mt-2 text-[var(--color-text-tertiary)] max-w-sm',
            sizeConfig.description
          )}
        >
          {finalDescription}
        </p>
      )}

      {/* Action */}
      {action && (
        <div className='mt-6'>
          <Button
            color={action.color ?? 'secondary'}
            size='md'
            onClick={action.onClick}
            iconLeading={action.icon}
            isDisabled={action.isDisabled}
            disabledTooltip={action.disabledTooltip}
          >
            {action.label}
          </Button>
        </div>
      )}
    </motion.div>
  );
}
